import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AccessibilityService } from '../shared/accessibility.service';
import { AuthService } from 'app/services/authentication/auth.service';
import { Utils } from '@shared/utils.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { FeatureToggle, SoftwareOptionsForCompany } from '@shared/enums';
import { ClientDiscoveryService } from 'app/platform/services/client-discovery/client-discovery.service';
import { KnownComponentsNames } from './components-names';
import { CookieName } from '@nodeModules/@itero/auth-angular';
import { AuthCookiesService } from '@shared/authCookies.service';
import { SoftwareOptionsService } from './companySoftwareOptions/service/softwareOptions.service';
import { IKeyValuePair } from '@interfaces/IKeyValuePair';
import {map, catchError, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { TimberService } from '@logging/timber.service';
import { HttpHeaders } from '@angular/common/http';
import { PlatformLoginService } from 'app/services/platform-login/platform-login.service';
@Injectable()
export class AuthGuard  {
	constructor(
		private platformLoginService: PlatformLoginService,
		private accessibilityService: AccessibilityService,
		private authService: AuthService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private cookieAuthService: AuthCookiesService,
		private softwareOptionsService: SoftwareOptionsService,
		private timberService: TimberService,
		private utils: Utils
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		return this.canActivateRoute();
	}

	canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		return this.canActivateRoute();
	}

	private canActivateRoute(): boolean {
		if (this.authService.isAuthenticated() || this.utils.isScannerLoggedIn()) {
			return true;
		}
		const relativeLocation = this.accessibilityService.getCurrentRelativeUrl();
		const rxRedirectUrl = relativeLocation?.startsWith('/rx/redirect/');

		if (rxRedirectUrl) {
			const companyId = this.cookieAuthService.getCookie(CookieName.COMPANYID);
			const token = this.cookieAuthService.getCookie(CookieName.ITEROTOKEN);

			if (companyId && token) {
				this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
					take(1),
					map(() => this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.EnablePlatformSelector, false)),
					switchMap((enablePlatformSelector) => {
						if (enablePlatformSelector) {

							return this.authService.prepareGetUserCompanyDataObservable(this.getHeaders(token), Number(companyId)).pipe(
								map((data) => data.body),
								map((softwareOptions: IKeyValuePair) => {
									const useMiflPlatform = Object.values(softwareOptions).includes(SoftwareOptionsForCompany.cswo_UseMiflPlatform);
									return useMiflPlatform;
								}),
								catchError(error => {
									this.timberService.error(`Error obtaining or processing software option: ${error}`, { module: 'AuthGuard' });
									return of(false);
								})
							);
						} else {
							return of(false);
						}
					})
				).subscribe(useMiflPlatform => {
					if (useMiflPlatform) {
						ClientDiscoveryService.getClientApplication(KnownComponentsNames.MyiteroLab)
							.then(entApp => {
								const redirectBaseUrl = entApp?.url ?? null;
								window.location.href = `${redirectBaseUrl}${relativeLocation}`;
							})
							.catch(error => {
								this.timberService.error(`Failed to preload client app URL: ${error}`, { module: 'AuthGuard' });
							});
					}
				});
				return false;
			}
		}


		if (!relativeLocation || relativeLocation === '/' || relativeLocation === '/dist') {
			this.platformLoginService.navigateToLogin();
		} else {
			this.platformLoginService.navigateToLogin({ queryParams: { returnUrl: relativeLocation } });
		}
		return false;
	}

	private getHeaders(token): HttpHeaders {
		let headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		headers = headers.set('Authorization', `Bearer ${token}`);
		return headers;
	}
}